import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import { useWorkspaceId } from '../workspace.js';
import type {
  ApiInvite,
  ApiLink,
  ApiMe,
  ApiMember,
  ApiSignupCode,
  ApiSignupInvite,
  ApiStats,
} from './types.js';

export const meKey = ['me'] as const;
export const linksKey = (workspaceId: string | null, q?: string) =>
  ['links', workspaceId, q ?? ''] as const;
export const linkKey = (workspaceId: string | null, id: string) =>
  ['link', workspaceId, id] as const;
export const statsKey = (workspaceId: string | null, id: string, range: string) =>
  ['stats', workspaceId, id, range] as const;
export const membersKey = (workspaceId: string | null) =>
  ['members', workspaceId] as const;
export const invitesKey = (workspaceId: string | null) =>
  ['invites', workspaceId] as const;

export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: () => api<ApiMe>('/auth/me'),
    retry: false,
  });
}

export function useLinks(q: string) {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: linksKey(workspaceId, q),
    enabled: !!workspaceId,
    queryFn: () =>
      api<{ links: ApiLink[] }>(`/links${q ? `?q=${encodeURIComponent(q)}` : ''}`, {
        workspaceId,
      }).then((r) => r.links),
  });
}

export function useLink(id: string | undefined) {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: linkKey(workspaceId, id ?? ''),
    enabled: !!workspaceId && !!id,
    queryFn: () =>
      api<{ link: ApiLink }>(`/links/${id}`, { workspaceId }).then((r) => r.link),
  });
}

export function useLinkStats(id: string | undefined, range: '7d' | '30d' | '90d' | 'all') {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: statsKey(workspaceId, id ?? '', range),
    enabled: !!workspaceId && !!id,
    queryFn: () => api<ApiStats>(`/links/${id}/stats?range=${range}`, { workspaceId }),
  });
}

export function useCreateLink() {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceId();
  return useMutation({
    mutationFn: (input: {
      destination_url: string;
      slug?: string;
      title?: string;
      expires_at?: string;
      max_clicks?: number;
    }) =>
      api<{ link: ApiLink }>('/links', {
        method: 'POST',
        body: input,
        workspaceId,
      }).then((r) => r.link),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['links', workspaceId] }),
  });
}

export function useUpdateLink(id: string) {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceId();
  return useMutation({
    mutationFn: (input: Partial<{
      destination_url: string;
      title: string;
      is_active: boolean;
      expires_at: string | null;
      max_clicks: number | null;
    }>) =>
      api<{ link: ApiLink }>(`/links/${id}`, {
        method: 'PATCH',
        body: input,
        workspaceId,
      }).then((r) => r.link),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['links', workspaceId] });
      qc.invalidateQueries({ queryKey: linkKey(workspaceId, id) });
    },
  });
}

export function useDeleteLink() {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceId();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/links/${id}`, { method: 'DELETE', workspaceId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['links', workspaceId] }),
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api<{ workspace: { id: string; name: string; role: 'owner' } }>(
        '/workspace',
        { method: 'POST', body: input },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: meKey }),
  });
}

export function useRenameWorkspace() {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceId();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api<{ workspace: { id: string; name: string } }>('/workspace', {
        method: 'PATCH',
        body: input,
        workspaceId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: meKey }),
  });
}

export function useDeleteWorkspace() {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceId();
  return useMutation({
    mutationFn: (input: { confirm_name: string }) =>
      api<{ ok: true }>('/workspace', {
        method: 'DELETE',
        body: input,
        workspaceId,
      }),
    onSuccess: () => qc.clear(),
  });
}

export function useMembers() {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: membersKey(workspaceId),
    enabled: !!workspaceId,
    queryFn: () =>
      api<{ members: ApiMember[] }>('/workspace/members', { workspaceId }).then(
        (r) => r.members,
      ),
  });
}

export function useInvites() {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: invitesKey(workspaceId),
    enabled: !!workspaceId,
    queryFn: () =>
      api<{ invites: ApiInvite[] }>('/invites', { workspaceId }).then((r) => r.invites),
  });
}

export function useCreateInvite() {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceId();
  return useMutation({
    mutationFn: (input: { email: string; role: 'admin' | 'member' }) =>
      api<{ ok: true; invite: ApiInvite }>('/invites', {
        method: 'POST',
        body: input,
        workspaceId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitesKey(workspaceId) }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceId();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/invites/${id}`, { method: 'DELETE', workspaceId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitesKey(workspaceId) }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceId();
  return useMutation({
    mutationFn: (userId: string) =>
      api<{ ok: true }>(`/workspace/members/${userId}`, {
        method: 'DELETE',
        workspaceId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: membersKey(workspaceId) }),
  });
}

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: (input: { email: string; turnstileToken?: string | null }) =>
      api<{ ok: true }>('/auth/request', {
        method: 'POST',
        body: {
          email: input.email,
          turnstileToken: input.turnstileToken ?? undefined,
        },
      }),
  });
}

export function useVerifyMagicLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<{ ok: true }>('/auth/verify', { method: 'POST', body: { token } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: meKey }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
    onSuccess: () => qc.clear(),
  });
}

// ----- signup invites -----

export const signupInvitesKey = ['signup-invites'] as const;

export function useSignupInvites() {
  return useQuery({
    queryKey: signupInvitesKey,
    queryFn: () =>
      api<{ invites: ApiSignupInvite[] }>('/signup-invites').then((r) => r.invites),
  });
}

export function useCreateSignupInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string }) =>
      api<{ ok: true; invite: ApiSignupInvite }>(
        '/signup-invites',
        { method: 'POST', body: input },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: signupInvitesKey }),
  });
}

export function useRevokeSignupInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/signup-invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: signupInvitesKey }),
  });
}

export function useAcceptSignupInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      token: string;
      name: string;
      workspaceName: string;
      turnstileToken?: string | null;
    }) =>
      api<{ ok: true; workspace: { id: string; name: string } }>(
        '/signup-invites/accept',
        {
          method: 'POST',
          body: {
            token: input.token,
            name: input.name,
            workspaceName: input.workspaceName,
            turnstileToken: input.turnstileToken ?? undefined,
          },
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: meKey }),
  });
}

// ----- signup codes -----

export const signupCodesKey = ['signup-codes'] as const;

export function useSignupCodes() {
  return useQuery({
    queryKey: signupCodesKey,
    queryFn: () =>
      api<{ codes: ApiSignupCode[] }>('/signup-codes').then((r) => r.codes),
  });
}

export function useCreateSignupCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      code?: string;
      maxUses?: number;
      expiresAt?: string;
      note?: string;
    }) =>
      api<{ ok: true; code: ApiSignupCode }>('/signup-codes', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: signupCodesKey }),
  });
}

export function useUpdateSignupCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; patch: Partial<{ is_active: boolean }> }) =>
      api<{ ok: true }>(`/signup-codes/${args.id}`, {
        method: 'PATCH',
        body: args.patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: signupCodesKey }),
  });
}

export function useDeleteSignupCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/signup-codes/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: signupCodesKey }),
  });
}

export function useRedeemSignupCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      code: string;
      email: string;
      name: string;
      workspaceName: string;
      turnstileToken?: string | null;
    }) =>
      api<{ ok: true; workspace: { id: string; name: string } }>(
        '/signup-codes/redeem',
        {
          method: 'POST',
          body: {
            code: input.code,
            email: input.email,
            name: input.name,
            workspaceName: input.workspaceName,
            turnstileToken: input.turnstileToken ?? undefined,
          },
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: meKey }),
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; name: string; turnstileToken?: string | null }) =>
      api<{ ok: true; workspace: { id: string; name: string } }>(
        '/invites/accept',
        {
          method: 'POST',
          body: {
            token: input.token,
            name: input.name,
            turnstileToken: input.turnstileToken ?? undefined,
          },
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: meKey }),
  });
}
